import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  GitBranch,
  GitCommit,
  GitMerge,
  Loader2,
  ChevronDown,
  Check,
  Plus,
  RefreshCw,
  Unlink,
} from 'lucide-react';
import { Button } from './Button';
import GithubIcon from './GithubIcon';
import { useGithubBranches, useGithubCommitHistory } from '../helpers/useGithubApi';
import { getConnectedRepo, setConnectedRepo, ConnectedRepoInfo } from './GithubImportTab';
import { CommitInfo } from '../endpoints/github/commits_GET.schema';
import { toast } from 'sonner';
import styles from './GitTreePanel.module.css';

// ============================================
// Multi-repo storage helpers
// ============================================
const MULTI_REPO_KEY = 'tasking-connected-repos';

type MultiRepoStore = Record<string, ConnectedRepoInfo[]>;

const getConnectedRepos = (sessionId: string): ConnectedRepoInfo[] => {
  try {
    const stored = localStorage.getItem(MULTI_REPO_KEY);
    if (stored) {
      const all: MultiRepoStore = JSON.parse(stored);
      if (all[sessionId]?.length) return all[sessionId];
    }
    // Fall back to single-repo storage for backward compat
    const single = getConnectedRepo(sessionId);
    return single ? [single] : [];
  } catch {
    return [];
  }
};

const setConnectedRepos = (sessionId: string, repos: ConnectedRepoInfo[]) => {
  try {
    const stored = localStorage.getItem(MULTI_REPO_KEY);
    const all: MultiRepoStore = stored ? JSON.parse(stored) : {};
    all[sessionId] = repos;
    localStorage.setItem(MULTI_REPO_KEY, JSON.stringify(all));
    // Also sync primary repo to old storage for GitPanel compat
    if (repos.length > 0) {
      setConnectedRepo(sessionId, repos[0]);
    }
  } catch {
    // Ignore
  }
};

// ============================================
// Graph lane assignment
// ============================================
const LANE_COLORS = [
  '#f97316', // orange (main)
  '#3b82f6', // blue
  '#a855f7', // purple
  '#22c55e', // green
  '#ef4444', // red
  '#eab308', // yellow
  '#06b6d4', // cyan
  '#ec4899', // pink
];

type GraphNode = {
  commit: CommitInfo;
  lane: number;
  color: string;
  parentLanes: { lane: number; color: string }[];
};

function buildGraph(commits: CommitInfo[]): GraphNode[] {
  if (!commits.length) return [];

  const shaToIndex = new Map<string, number>();
  commits.forEach((c, i) => shaToIndex.set(c.sha, i));

  // Track active lanes: each lane holds the SHA of the commit expected next in that lane
  const activeLanes: (string | null)[] = [];
  const nodes: GraphNode[] = [];

  for (let i = 0; i < commits.length; i++) {
    const commit = commits[i];

    // Find which lane this commit belongs to (if any lane is expecting it)
    let lane = activeLanes.indexOf(commit.sha);

    if (lane === -1) {
      // No lane expects this commit — assign to first empty lane or create new
      const emptyLane = activeLanes.indexOf(null);
      if (emptyLane !== -1) {
        lane = emptyLane;
      } else {
        lane = activeLanes.length;
        activeLanes.push(null);
      }
    }

    // This lane now expects the first parent (if any)
    if (commit.parents.length > 0) {
      activeLanes[lane] = commit.parents[0];
    } else {
      activeLanes[lane] = null;
    }

    // For merge commits (2+ parents), the second parent goes to a new or reused lane
    const parentLanes: { lane: number; color: string }[] = [];

    for (let p = 0; p < commit.parents.length; p++) {
      const parentSha = commit.parents[p];
      const existingLane = activeLanes.indexOf(parentSha);

      if (p === 0) {
        parentLanes.push({ lane, color: LANE_COLORS[lane % LANE_COLORS.length] });
      } else if (existingLane !== -1 && existingLane !== lane) {
        parentLanes.push({
          lane: existingLane,
          color: LANE_COLORS[existingLane % LANE_COLORS.length],
        });
      } else {
        // Assign second parent to a new lane
        const newLane = activeLanes.length;
        activeLanes.push(parentSha);
        parentLanes.push({
          lane: newLane,
          color: LANE_COLORS[newLane % LANE_COLORS.length],
        });
      }
    }

    // Clean up lanes that point to commits already processed
    for (let l = 0; l < activeLanes.length; l++) {
      if (activeLanes[l] && shaToIndex.has(activeLanes[l]!)) {
        const targetIdx = shaToIndex.get(activeLanes[l]!)!;
        if (targetIdx < i) {
          activeLanes[l] = null;
        }
      }
    }

    nodes.push({
      commit,
      lane,
      color: LANE_COLORS[lane % LANE_COLORS.length],
      parentLanes,
    });
  }

  return nodes;
}

// ============================================
// SVG Graph Rail
// ============================================
const LANE_WIDTH = 16;
const NODE_RADIUS = 4;
const ROW_HEIGHT = 32;

function GraphRailSVG({
  nodes,
  rowIndex,
  maxLanes,
}: {
  nodes: GraphNode[];
  rowIndex: number;
  maxLanes: number;
}) {
  const node = nodes[rowIndex];
  const width = Math.max((maxLanes + 1) * LANE_WIDTH, 48);
  const cx = node.lane * LANE_WIDTH + LANE_WIDTH / 2;
  const cy = ROW_HEIGHT / 2;

  // Draw lines from this commit to its parents
  const lines: React.ReactNode[] = [];

  // Vertical continuation lines for all active lanes
  for (let i = 0; i < maxLanes; i++) {
    // Check if this lane has a commit at current or adjacent row
    const hasAbove = rowIndex > 0 && nodes[rowIndex - 1].lane === i;
    const hasCurrent = node.lane === i;
    const hasBelow = rowIndex < nodes.length - 1 && nodes[rowIndex + 1].lane === i;

    // Check if any commit's parent passes through this lane
    let isActiveLane = false;
    for (let r = 0; r <= rowIndex; r++) {
      const n = nodes[r];
      for (const pl of n.parentLanes) {
        if (pl.lane === i) {
          // Check if the parent commit is below current row
          const parentIdx = nodes.findIndex(
            (nn) => n.commit.parents.includes(nn.commit.sha) && nn.lane === pl.lane,
          );
          if (parentIdx > rowIndex) {
            isActiveLane = true;
          }
        }
      }
    }

    if (isActiveLane && !hasCurrent) {
      const lx = i * LANE_WIDTH + LANE_WIDTH / 2;
      lines.push(
        <line
          key={`vline-${i}`}
          x1={lx}
          y1={0}
          x2={lx}
          y2={ROW_HEIGHT}
          stroke={LANE_COLORS[i % LANE_COLORS.length]}
          strokeWidth={2}
          opacity={0.4}
        />,
      );
    }
  }

  // Draw merge lines
  if (node.parentLanes.length > 1) {
    for (let p = 1; p < node.parentLanes.length; p++) {
      const pl = node.parentLanes[p];
      const px = pl.lane * LANE_WIDTH + LANE_WIDTH / 2;
      lines.push(
        <path
          key={`merge-${p}`}
          d={`M ${cx} ${cy} C ${cx} ${cy + 12}, ${px} ${cy + 4}, ${px} ${ROW_HEIGHT}`}
          fill="none"
          stroke={pl.color}
          strokeWidth={2}
          opacity={0.6}
        />,
      );
    }
  }

  // Vertical line from this node downward to first parent
  if (node.commit.parents.length > 0) {
    const nextNode = nodes.find((n) => n.commit.sha === node.commit.parents[0]);
    if (nextNode) {
      const nx = nextNode.lane * LANE_WIDTH + LANE_WIDTH / 2;
      if (nx === cx) {
        // Straight down
        lines.push(
          <line
            key="down"
            x1={cx}
            y1={cy}
            x2={cx}
            y2={ROW_HEIGHT}
            stroke={node.color}
            strokeWidth={2}
            opacity={0.6}
          />,
        );
      } else {
        // Curve to different lane
        lines.push(
          <path
            key="down-curve"
            d={`M ${cx} ${cy} C ${cx} ${cy + 12}, ${nx} ${cy + 4}, ${nx} ${ROW_HEIGHT}`}
            fill="none"
            stroke={node.color}
            strokeWidth={2}
            opacity={0.6}
          />,
        );
      }
    }
  }

  // Vertical line from above into this node
  if (rowIndex > 0) {
    const prevNode = nodes[rowIndex - 1];
    // Check if previous node's first parent is this commit
    if (prevNode.commit.parents[0] === node.commit.sha && prevNode.lane === node.lane) {
      lines.push(
        <line
          key="up"
          x1={cx}
          y1={0}
          x2={cx}
          y2={cy}
          stroke={node.color}
          strokeWidth={2}
          opacity={0.6}
        />,
      );
    }
  }

  return (
    <svg
      width={width}
      height={ROW_HEIGHT}
      className={styles.graphRail}
      style={{ width, minWidth: width }}
    >
      {lines}
      {/* Node circle */}
      <circle
        cx={cx}
        cy={cy}
        r={NODE_RADIUS}
        fill={node.color}
        stroke={node.color}
        strokeWidth={rowIndex === 0 ? 3 : 0}
        opacity={1}
      />
      {rowIndex === 0 && (
        <circle
          cx={cx}
          cy={cy}
          r={NODE_RADIUS + 3}
          fill="none"
          stroke={node.color}
          strokeWidth={1.5}
          opacity={0.5}
        />
      )}
    </svg>
  );
}

// ============================================
// Time ago helper
// ============================================
function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

// ============================================
// Props
// ============================================
interface GitTreePanelProps {
  sessionId: string;
  files: Array<{
    id: string;
    fileName: string;
    filePath: string;
    content: string;
  }>;
  onRefreshFiles?: () => void;
}

// ============================================
// Component
// ============================================
export const GitTreePanel: React.FC<GitTreePanelProps> = ({ sessionId, files, onRefreshFiles }) => {
  const [repos, setRepos] = useState<ConnectedRepoInfo[]>([]);
  const [activeRepoIndex, setActiveRepoIndex] = useState(0);
  const [showRepoDropdown, setShowRepoDropdown] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState<string | null>(null);
  const [showBranchDropdown, setShowBranchDropdown] = useState(false);
  const [view, setView] = useState<'graph' | 'commit'>('graph');

  const activeRepo = repos[activeRepoIndex] || null;

  // Load repos on mount
  useEffect(() => {
    const loaded = getConnectedRepos(sessionId);
    setRepos(loaded);
    if (loaded.length > 0 && loaded[0].branch) {
      setSelectedBranch(loaded[0].branch);
    }
  }, [sessionId]);

  // Fetch branches
  const { data: branchesData, isLoading: branchesLoading } = useGithubBranches(
    activeRepo?.repo,
    !!activeRepo,
  );

  // Fetch commit history
  const {
    data: commitsData,
    isLoading: commitsLoading,
    refetch: refetchCommits,
  } = useGithubCommitHistory(activeRepo?.repo, selectedBranch || undefined, !!activeRepo);

  // Build graph nodes
  const graphNodes = useMemo(() => {
    if (!commitsData?.commits) return [];
    return buildGraph(commitsData.commits);
  }, [commitsData]);

  const maxLanes = useMemo(() => {
    if (!graphNodes.length) return 1;
    return Math.max(...graphNodes.map((n) => n.lane)) + 1;
  }, [graphNodes]);

  // Branch selection
  const handleBranchSelect = useCallback(
    (branchName: string) => {
      setSelectedBranch(branchName);
      setShowBranchDropdown(false);
      if (activeRepo) {
        const updated = repos.map((r, i) =>
          i === activeRepoIndex ? { ...r, branch: branchName } : r,
        );
        setRepos(updated);
        setConnectedRepos(sessionId, updated);
      }
    },
    [activeRepo, repos, activeRepoIndex, sessionId],
  );

  // Repo selection
  const handleRepoSelect = useCallback(
    (index: number) => {
      setActiveRepoIndex(index);
      setShowRepoDropdown(false);
      const repo = repos[index];
      if (repo) {
        setSelectedBranch(repo.branch);
      }
    },
    [repos],
  );

  // Add repo (opens GitHub import)
  const handleAddRepo = useCallback(() => {
    setShowRepoDropdown(false);
    window.dispatchEvent(new CustomEvent('tasking.openGithubImport'));
  }, []);

  // Disconnect current repo
  const handleDisconnect = useCallback(() => {
    const updated = repos.filter((_, i) => i !== activeRepoIndex);
    setRepos(updated);
    setConnectedRepos(sessionId, updated);
    setActiveRepoIndex(0);
    if (updated.length > 0) {
      setSelectedBranch(updated[0].branch);
    }
    toast.success('Repository disconnected');
  }, [repos, activeRepoIndex, sessionId]);

  // Refresh on mount and when repos change
  useEffect(() => {
    const handler = () => {
      const loaded = getConnectedRepos(sessionId);
      setRepos(loaded);
    };
    window.addEventListener('tasking.repoConnected', handler);
    return () => window.removeEventListener('tasking.repoConnected', handler);
  }, [sessionId]);

  // No connected repos
  if (repos.length === 0) {
    return (
      <div className={styles.emptyState}>
        <GithubIcon size={48} className={styles.emptyIcon} connected={false} />
        <h3>No Repositories Connected</h3>
        <p>Import a repository from GitHub to see the commit graph and manage multiple repos.</p>
        <Button size="sm" onClick={handleAddRepo}>
          <Plus size={14} />
          Connect Repository
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        {/* Repo row */}
        <div className={styles.repoRow}>
          <div className={styles.repoSelector}>
            <button
              className={styles.repoButton}
              onClick={() => setShowRepoDropdown(!showRepoDropdown)}
            >
              <GithubIcon size={14} connected={true} />
              <span className={styles.repoName}>{activeRepo?.repo || 'Select repo'}</span>
              <ChevronDown size={12} />
            </button>
            {showRepoDropdown && (
              <div className={styles.repoDropdown}>
                {repos.map((repo, i) => (
                  <button
                    key={repo.repo}
                    className={`${styles.repoOption} ${i === activeRepoIndex ? styles.repoOptionActive : ''}`}
                    onClick={() => handleRepoSelect(i)}
                  >
                    {i === activeRepoIndex && <Check size={12} />}
                    <span>{repo.repo}</span>
                  </button>
                ))}
                <button
                  className={`${styles.repoOption} ${styles.addRepoOption}`}
                  onClick={handleAddRepo}
                >
                  <Plus size={12} />
                  <span>Add repository...</span>
                </button>
              </div>
            )}
          </div>
          <Button variant="ghost" size="icon-sm" onClick={() => refetchCommits()} title="Refresh">
            <RefreshCw size={13} />
          </Button>
          <Button variant="ghost" size="icon-sm" onClick={handleDisconnect} title="Disconnect">
            <Unlink size={13} />
          </Button>
        </div>

        {/* Branch + view toggle row */}
        <div className={styles.branchRow}>
          <div className={styles.branchSelector}>
            <button
              className={styles.branchButton}
              onClick={() => setShowBranchDropdown(!showBranchDropdown)}
              disabled={branchesLoading}
            >
              <GitBranch size={12} />
              <span>{selectedBranch || 'Loading...'}</span>
              <ChevronDown size={10} />
            </button>
            {showBranchDropdown && branchesData && (
              <div className={styles.branchDropdown}>
                {branchesData.branches.map((branch) => (
                  <button
                    key={branch.name}
                    className={`${styles.branchOption} ${branch.name === selectedBranch ? styles.branchOptionActive : ''}`}
                    onClick={() => handleBranchSelect(branch.name)}
                  >
                    {branch.name === selectedBranch && <Check size={12} />}
                    <span>{branch.name}</span>
                    {branch.protected && <span className={styles.protectedBadge}>protected</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className={styles.viewToggle}>
            <button
              className={`${styles.viewToggleBtn} ${view === 'graph' ? styles.viewToggleBtnActive : ''}`}
              onClick={() => setView('graph')}
            >
              Graph
            </button>
            <button
              className={`${styles.viewToggleBtn} ${view === 'commit' ? styles.viewToggleBtnActive : ''}`}
              onClick={() => setView('commit')}
            >
              Commit
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      {commitsLoading ? (
        <div className={styles.loadingState}>
          <Loader2 size={24} className={styles.spinner} />
          <span>Loading commits...</span>
        </div>
      ) : view === 'graph' ? (
        /* Graph view */
        <div className={styles.graphContainer}>
          {graphNodes.map((node, i) => (
            <div key={node.commit.sha} className={styles.commitRow}>
              <GraphRailSVG nodes={graphNodes} rowIndex={i} maxLanes={maxLanes} />
              <div className={styles.commitInfo}>
                <div className={styles.commitMessage}>
                  {i === 0 && <span className={styles.headBadge}>HEAD</span>}{' '}
                  {node.commit.parents.length > 1 && (
                    <span className={styles.mergeBadge}>
                      <GitMerge size={9} />
                      merge
                    </span>
                  )}{' '}
                  {node.commit.message.split('\n')[0]}
                </div>
                <div className={styles.commitMeta}>
                  <span className={styles.commitSha}>{node.commit.shortSha}</span>
                  <span className={styles.commitAuthor}>
                    {node.commit.authorAvatar && (
                      <img src={node.commit.authorAvatar} alt="" className={styles.authorAvatar} />
                    )}
                    {node.commit.authorName}
                  </span>
                  <span className={styles.commitDate}>
                    {node.commit.date ? timeAgo(node.commit.date) : ''}
                  </span>
                </div>
              </div>
            </div>
          ))}
          {graphNodes.length === 0 && (
            <div
              className={styles.emptyState}
              style={{ height: 'auto', padding: 'var(--spacing-8)' }}
            >
              <GitCommit size={32} style={{ opacity: 0.3 }} />
              <p>No commits found on this branch.</p>
            </div>
          )}
        </div>
      ) : (
        /* Commit view - reuse existing GitPanel */
        <CommitView sessionId={sessionId} files={files} onRefreshFiles={onRefreshFiles} />
      )}
    </div>
  );
};

// ============================================
// Inline commit view (wraps existing GitPanel)
// ============================================
function CommitView({
  sessionId,
  files,
  onRefreshFiles,
}: {
  sessionId: string;
  files: GitTreePanelProps['files'];
  onRefreshFiles?: () => void;
}) {
  // Lazy import to avoid circular deps
  const [Panel, setPanel] = useState<React.ComponentType<any> | null>(null);
  useEffect(() => {
    import('./GitPanel').then((mod) => {
      setPanel(() => mod.GitPanel);
    });
  }, []);

  if (!Panel) {
    return (
      <div className={styles.loadingState}>
        <Loader2 size={20} className={styles.spinner} />
      </div>
    );
  }

  return <Panel sessionId={sessionId} files={files} onRefreshFiles={onRefreshFiles} />;
}
